import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  LinearProgress,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  Divider,
  ListItemIcon,
  useTheme,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import DownloadIcon from '@mui/icons-material/Download';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SavingsIcon from '@mui/icons-material/Savings';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import PetsIcon from '@mui/icons-material/Pets';
import { styled } from '@mui/material/styles';
import { supabase } from '../../../lib/supabase';
import dayjs from 'dayjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

/**
 * ROI Projection Component
 * Calculates and visualizes Return on Investment based on current trends
 */
const ROIProjectionCard = ({ netIncome, totalExpenses, trend, onExport }: { netIncome: number, totalExpenses: number, trend: number, onExport?: () => void }) => {
  const theme = useTheme();
  
  // Simple ROI calculation
  const roi = totalExpenses > 0 ? ((netIncome / totalExpenses) * 100).toFixed(1) : '0.0';
  const isPositive = parseFloat(roi) > 0;
  
  return (
    <Card elevation={0} sx={{ 
      p: 3, 
      borderRadius: 4, 
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
      color: 'white',
      height: '100%',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Box sx={{ position: 'absolute', right: -20, top: -20, opacity: 0.1 }}>
        <PaidIcon sx={{ fontSize: 180 }} />
      </Box>
      
      <Box position="relative" zIndex={1}>
        <Typography variant="subtitle2" sx={{ opacity: 0.8, mb: 1 }}>
          Current ROI Performance
        </Typography>
        <Box display="flex" alignItems="baseline" gap={1} mb={2}>
          <Typography variant="h2" fontWeight="800">
            {roi}%
          </Typography>
          <Chip 
            label={isPositive ? "Profitable" : "Deficit"} 
            size="small" 
            sx={{ 
              bgcolor: isPositive ? 'rgba(255,255,255,0.2)' : 'rgba(255,0,0,0.2)', 
              color: 'white',
              fontWeight: 'bold'
            }} 
          />
        </Box>
        
        <Box mb={3}>
          <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
            Projected Annual Yield
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            + $ {((netIncome * 12) * 1.15).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Based on {trend > 0 ? '+' : ''}{trend}% growth trajectory
          </Typography>
        </Box>

        <Button 
          variant="contained" 
          sx={{ 
            bgcolor: 'white', 
            color: 'primary.main',
            fontWeight: 'bold',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
          }}
          startIcon={<DownloadIcon />}
          onClick={onExport}
        >
          Export Financial Report
        </Button>
      </Box>
    </Card>
  );
};

/**
 * Transaction interface defining the structure of financial transactions
 * Used to track income and expenses for the farm
 */
interface Transaction {
  id: string;
  date: string;
  type: 'income' | 'expense';
  category: string;
  amount: number;
  description: string;
  paymentMethod: string;
  reference?: string;
  status: 'pending' | 'completed' | 'cancelled';
}

/**
 * Budget interface defining the structure of budget items
 * Used to track allocated funds, expenditures, and remaining balances by category
 */
interface Budget {
  id: string;
  category: string;
  allocated: number;
  spent: number;
  remaining: number;
  period: string;
}

/**
 * Props interface for the FarmFinances component
 */
interface FarmFinancesProps {
  farm: any;
}

/**
 * Predefined categories for income and expense transactions
 * Used to populate dropdown options when creating/editing transactions
 */
const transactionCategories = {
  income: [
    'Crop Sales',
    'Livestock Sales',
    'Product Sales',
    'Services',
    'Grants',
    'Other Income',
  ],
  expense: [
    'Seeds',
    'Fertilizers',
    'Pesticides',
    'Feed',
    'Labor',
    'Equipment',
    'Maintenance',
    'Utilities',
    'Transportation',
    'Marketing',
    'Insurance',
    'Other Expenses',
  ],
};

/**
 * Available payment methods for transactions
 * Used for dropdown options when creating/editing transactions
 */
const paymentMethods = [
  'Cash',
  'M-PESA',
  'Bank Transfer',
  'Credit Card',
  'Check',
];

/**
 * Styled Card component for consistent card styling throughout the component
 */
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

const SummaryCard = styled(StyledCard)<{ type?: 'income' | 'expense' | 'net' }>(({ theme, type }) => {
  let bg = theme.palette.background.paper;
  let color = theme.palette.text.primary;
  
  if (type === 'income') {
    bg = alpha(theme.palette.success.main, 0.1);
    color = theme.palette.success.dark;
  } else if (type === 'expense') {
    bg = alpha(theme.palette.error.main, 0.1);
    color = theme.palette.error.dark;
  } else if (type === 'net') {
    bg = alpha(theme.palette.primary.main, 0.1);
    color = theme.palette.primary.dark;
  }

  return {
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${bg} 100%)`,
    '& .icon-wrapper': {
      backgroundColor: bg,
      borderRadius: '50%',
      padding: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: color,
    }
  };
});

/**
 * Props interface for the TabPanel component
 */
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

/**
 * TabPanel component for managing the tab content visibility
 * Displays children only when the current tab value matches the index
 */
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`finance-tabpanel-${index}`}
      aria-labelledby={`finance-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

/**
 * FarmFinances component
 * Manages financial information for a farm including transactions and budgets
 * Provides interfaces for viewing, adding, editing, and deleting financial entries
 */
const FarmFinances: React.FC<FarmFinancesProps> = ({ farm }) => {
  const theme = useTheme();
  
  /**
   * State for storing transactions
   * Currently initialized with sample data
   * Will be replaced with data from Superbase in production
   */
  const [transactions, setTransactions] = useState<Transaction[]>([]);

  /**
   * State for storing budget data
   * Will be replaced with data from Superbase in production
   */
  const [budgets, setBudgets] = useState<Budget[]>([]);

  /**
   * State for tracking the currently active tab
   * 0 = Transactions tab, 1 = Budget tab
   */
  const [tabValue, setTabValue] = useState(0);
  
  /**
   * State for controlling the transaction dialog visibility
   */
  const [openDialog, setOpenDialog] = useState(false);
  const [budgetDialog, setBudgetDialog] = useState(false);
  
  /**
   * State for tracking the transaction being edited
   * null when adding a new transaction
   */
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  
  /**
   * State for the transaction form data
   * Used for both adding new transactions and editing existing ones
   */
  const [newTransaction, setNewTransaction] = useState<Partial<Transaction>>({
    date: '',
    type: 'expense',
    category: '',
    amount: 0,
    description: '',
    paymentMethod: '',
    reference: '',
    status: 'pending',
  });

  const [newBudget, setNewBudget] = useState<Partial<Budget>>({
    category: '',
    allocated: 0,
    spent: 0,
    period: new Date().getFullYear().toString(),
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Asset States
  const [assetStats, setAssetStats] = useState({
    livestockValue: 0,
    inventoryAssetValue: 0,
    inventoryConsumableValue: 0,
    totalAssetValue: 0,
  });

  // Modern UI States
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'income' | 'expense'>('all');
  
  // Context Menu State
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);

  const farmId = farm?.id;

  const fetchData = async () => {
    if (!farmId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch Transactions
      const { data: transData, error: transError } = await supabase
        .from('farm_transactions')
        .select('*')
        .eq('farm_id', farmId)
        .order('date', { ascending: false });
      
      if (transError) throw transError;

      if (transData) {
        setTransactions(transData.map((t: any) => ({
          id: t.id,
          date: t.date,
          type: t.type,
          category: t.category,
          amount: Number(t.amount),
          description: t.description,
          paymentMethod: t.payment_method || '',
          reference: t.reference || '',
          status: t.status || 'completed',
        })));
      }

      // Fetch Budgets
      const { data: budgetData, error: budgetError } = await supabase
        .from('farm_budgets')
        .select('*')
        .eq('farm_id', farmId);

      if (budgetError) throw budgetError;

      if (budgetData) {
        // Helper to calculate actual spent from transactions
        const calculateSpent = (category: string, period: string) => {
          if (!transData) return 0;
          return transData
            .filter((t: any) => 
              t.type === 'expense' && 
              t.category === category &&
              t.date.startsWith(period) // Match year "2024" with "2024-05-12"
            )
            .reduce((sum: number, t: any) => sum + Number(t.amount), 0);
        };

        setBudgets(budgetData.map((b: any) => {
          // Use calculated spent if available, otherwise fallback to DB value
          // This makes the "Spent" column dynamic based on actual transactions
          const realSpent = calculateSpent(b.category, b.period);
          
          return {
            id: b.id,
            category: b.category,
            allocated: Number(b.allocated),
            spent: realSpent, 
            remaining: Number(b.allocated) - realSpent,
            period: b.period,
          };
        }));
      }

      // Fetch Assets (Livestock & Inventory)
      // 1. Livestock Value
      const { data: animalsData, error: animalsError } = await supabase
        .from('animals')
        .select('current_value')
        .eq('farm_id', farmId);
      
      let livestockVal = 0;
      if (animalsData && !animalsError) {
        livestockVal = animalsData.reduce((sum: number, animal: any) => sum + (Number(animal.current_value) || 0), 0);
      }

      // 2. Inventory Value (Assets & Consumables)
      const { data: inventoryData, error: inventoryError } = await supabase
        .from('inventory')
        .select('cost, quantity, is_asset, useful_life_years, salvage_value, purchase_date')
        .eq('farm_id', farmId);

      let invAssetVal = 0;
      let invConsumableVal = 0;

      if (inventoryData && !inventoryError) {
        inventoryData.forEach((item: any) => {
          const cost = Number(item.cost) || 0;
          const qty = Number(item.quantity) || 0;
          
          if (item.is_asset && item.useful_life_years && item.purchase_date) {
            // Depreciated Asset Calculation (Straight Line)
            const purchaseDate = dayjs(item.purchase_date);
            const now = dayjs();
            const yearsHeld = now.diff(purchaseDate, 'year', true);
            const usefulLife = Number(item.useful_life_years);
            const salvage = Number(item.salvage_value || 0);
            
            if (usefulLife > 0) {
              const depreciationPerYear = (cost - salvage) / usefulLife;
              const totalDepreciation = depreciationPerYear * yearsHeld;
              const currentValue = Math.max(salvage, cost - totalDepreciation);
              invAssetVal += currentValue * qty;
            } else {
              invAssetVal += cost * qty;
            }
          } else {
            // Consumable / Standard Inventory
            invConsumableVal += cost * qty;
          }
        });
      }

      setAssetStats({
        livestockValue: livestockVal,
        inventoryAssetValue: invAssetVal,
        inventoryConsumableValue: invConsumableVal,
        totalAssetValue: livestockVal + invAssetVal + invConsumableVal,
      });

    } catch (err: any) {
      console.error('Failed to fetch finance data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [farmId]);

  /**
   * Opens the dialog to add a new transaction
   * Initializes the editing transaction state with default values
   */
  const handleAddTransaction = () => {
    setEditingTransaction(null);
    setNewTransaction({
      date: new Date().toISOString().split('T')[0],
      type: 'expense',
      category: '',
      amount: 0,
      description: '',
      paymentMethod: '',
      reference: '',
      status: 'pending',
    });
    setOpenDialog(true);
  };

  /**
   * Opens the dialog to edit an existing transaction
   * Populates the form with the selected transaction's data
   * @param transaction - The transaction to be edited
   */
  const handleEditTransaction = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    setNewTransaction(transaction);
    setOpenDialog(true);
    handleMenuClose();
  };

  /**
   * Deletes a transaction from the transactions list
   * @param transactionId - The ID of the transaction to delete
   */
  const handleDeleteTransaction = async (transactionId: string) => {
    handleMenuClose();
    // Optimistic update
    const prevTransactions = [...transactions];
    setTransactions(prevTransactions.filter(t => t.id !== transactionId));

    try {
      const { error: deleteError } = await supabase
        .from('farm_transactions')
        .delete()
        .eq('id', transactionId);
      
      if (deleteError) throw deleteError;
    } catch (err: any) {
      setError(err.message || 'Failed to delete transaction.');
      setTransactions(prevTransactions); // Revert
    }
  };

  /**
   * Saves the current transaction being edited
   * Updates existing transaction or adds new transaction to the list
   * In production, this will update the Superbase database
   */
  const handleSaveTransaction = async () => {
    setLoading(true);
    setError(null);
    
    // Create a temporary ID for optimistic update
    const tempId = Math.random().toString(36).substring(7);
    const transactionData = {
      ...newTransaction,
      id: editingTransaction ? editingTransaction.id : tempId,
    } as Transaction;

    try {
      const payload = {
        farm_id: farmId,
        date: newTransaction.date,
        type: newTransaction.type,
        category: newTransaction.category,
        amount: newTransaction.amount,
        description: newTransaction.description,
        payment_method: newTransaction.paymentMethod,
        reference: newTransaction.reference,
        status: newTransaction.status,
      };

      if (editingTransaction) {
        // Optimistic update
        setTransactions(transactions.map(t => t.id === editingTransaction.id ? transactionData : t));
        
        const { error: updateError } = await supabase
          .from('farm_transactions')
          .update(payload)
          .eq('id', editingTransaction.id);
        if (updateError) throw updateError;
      } else {
        // Optimistic update
        setTransactions([transactionData, ...transactions]);

        const { data, error: insertError } = await supabase
          .from('farm_transactions')
          .insert([payload])
          .select();
        
        if (insertError) throw insertError;
        
        // Update with real ID from DB
        if (data && data[0]) {
           setTransactions(prev => prev.map(t => t.id === tempId ? { ...t, id: data[0].id } : t));
        }
      }
      setOpenDialog(false);
      // Refresh to ensure budgets are updated if expenses affect them (future feature)
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to save transaction.');
      // Refresh to ensure consistency
    } finally {
      setLoading(false);
    }
  };

  const handleAddBudget = () => {
    setNewBudget({
      category: '',
      allocated: 0,
      spent: 0,
      period: new Date().getFullYear().toString(),
    });
    setBudgetDialog(true);
  };

  const handleSaveBudget = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        farm_id: farmId,
        category: newBudget.category,
        allocated: newBudget.allocated,
        spent: 0, // Initially 0, or calculated from transactions
        period: newBudget.period,
      };

      const { error } = await supabase
        .from('farm_budgets')
        .insert([payload]);
      
      if (error) throw error;
      
      setBudgetDialog(false);
      fetchData(); // Refresh to show new budget
    } catch (err: any) {
      setError(err.message || 'Failed to save budget.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates a field in the transaction form
   * @param field - The field to update
   * @param value - The new value for the field
   */
  const handleInputChange = (field: keyof Transaction, value: any) => {
    setNewTransaction({ ...newTransaction, [field]: value });
  };

  /**
   * Handles tab changes between Transactions and Budget views
   * @param event - The event triggering the tab change
   * @param newValue - The index of the selected tab
   */
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, transactionId: string) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedTransactionId(transactionId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedTransactionId(null);
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    const matchesSearch = 
      t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.paymentMethod.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterType === 'all' || t.type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  /**
   * Calculates financial summary from transactions
   * Only includes completed transactions in the calculations
   * @returns Object with totalIncome, totalExpenses, and netIncome
   */
  const getFinancialSummary = () => {
    const summary = transactions.reduce(
      (acc, transaction) => {
        if (transaction.status === 'completed') {
          if (transaction.type === 'income') {
            acc.totalIncome += transaction.amount;
          } else {
            acc.totalExpenses += transaction.amount;
          }
        }
        return acc;
      },
      { totalIncome: 0, totalExpenses: 0 }
    );
    return {
      ...summary,
      netIncome: summary.totalIncome - summary.totalExpenses,
    };
  };

  /**
   * Calculates budget summary from budgets
   * Aggregates allocated, spent, and remaining amounts across all budget categories
   * @returns Object with totalAllocated, totalSpent, and totalRemaining
   */
  const getBudgetSummary = () => {
    return budgets.reduce(
      (acc, budget) => {
        acc.totalAllocated += budget.allocated;
        acc.totalSpent += budget.spent;
        acc.totalRemaining += budget.remaining;
        return acc;
      },
      { totalAllocated: 0, totalSpent: 0, totalRemaining: 0 }
    );
  };

  // Calculate financial summaries for display
  const summary = getFinancialSummary();
  const budgetSummary = getBudgetSummary();
  
  // Calculate trend (mock logic for demo: compares current month vs last month)
  // In real app, this would be calculated from real historical data comparison
  const financialTrend = 12.5; 

  // Prepare Chart Data
  const monthlyData = transactions.reduce((acc: any, t) => {
    const month = new Date(t.date).toLocaleString('default', { month: 'short' });
    if (!acc[month]) acc[month] = { income: 0, expense: 0 };
    if (t.type === 'income') acc[month].income += t.amount;
    else acc[month].expense += t.amount;
    return acc;
  }, {});

  const chartLabels = Object.keys(monthlyData).reverse(); // Assuming simple sort for now
  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Income',
        data: chartLabels.map(m => monthlyData[m].income),
        backgroundColor: theme.palette.success.main,
        borderRadius: 4,
      },
      {
        label: 'Expenses',
        data: chartLabels.map(m => monthlyData[m].expense),
        backgroundColor: theme.palette.error.main,
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const },
    },
    scales: {
      y: { beginAtZero: true },
      x: { grid: { display: false } }
    },
  };

  const expenseCategories = transactions
    .filter(t => t.type === 'expense')
    .reduce((acc: any, t) => {
      acc[t.category] = (acc[t.category] || 0) + t.amount;
      return acc;
    }, {});
  
  const doughnutData = {
    labels: Object.keys(expenseCategories),
    datasets: [
      {
        data: Object.values(expenseCategories),
        backgroundColor: [
          theme.palette.primary.main,
          theme.palette.secondary.main,
          theme.palette.error.main,
          theme.palette.warning.main,
          theme.palette.info.main,
          theme.palette.success.main,
        ],
        borderWidth: 1,
      },
    ],
  };

  const assetChartData = {
    labels: ['Livestock', 'Fixed Assets', 'Consumables'],
    datasets: [
      {
        data: [assetStats.livestockValue, assetStats.inventoryAssetValue, assetStats.inventoryConsumableValue],
        backgroundColor: [
          theme.palette.primary.main, // Livestock
          theme.palette.warning.main, // Fixed Assets
          theme.palette.info.main,    // Consumables
        ],
        borderWidth: 1,
      },
    ],
  };

  const handleExport = () => {
    // 1. Prepare Data
    const headers = ['Date', 'Type', 'Category', 'Description', 'Amount', 'Payment Method', 'Status'];
    const csvRows = [headers.join(',')];

    transactions.forEach(t => {
      const row = [
        t.date,
        t.type,
        t.category,
        `"${t.description.replace(/"/g, '""')}"`,
        t.amount,
        t.paymentMethod,
        t.status
      ];
      csvRows.push(row.join(','));
    });

    // Add Asset Summary Section to CSV
    csvRows.push('');
    csvRows.push('ASSET VALUATION SUMMARY');
    csvRows.push('Category,Value');
    csvRows.push(`Livestock,${assetStats.livestockValue}`);
    csvRows.push(`Fixed Assets,${assetStats.inventoryAssetValue}`);
    csvRows.push(`Consumables,${assetStats.inventoryConsumableValue}`);
    csvRows.push(`Total Assets,${assetStats.totalAssetValue}`);

    const csvContent = csvRows.join('\n');

    // 2. Create Download Link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `farm_financial_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (loading && transactions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">Financial Performance</Typography>
        <Stack direction="row" spacing={2}>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />}
            onClick={handleExport}
          >
            Export Report
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={handleAddTransaction}
            sx={{ boxShadow: theme.shadows[4] }}
          >
            Add Transaction
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Grid container spacing={3} mb={4}>
        {/* ROI Card - The "Investor" Highlight */}
        <Grid item xs={12} md={4}>
          <ROIProjectionCard 
            netIncome={summary.netIncome} 
            totalExpenses={summary.totalExpenses}
            trend={financialTrend}
            onExport={handleExport}
          />
        </Grid>

        {/* Standard Metrics */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3} height="100%">
            <Grid item xs={12} sm={6}>
              <SummaryCard type="income">
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box>
                      <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                        Total Revenue
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ${summary.totalIncome.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box className="icon-wrapper">
                      <TrendingUpIcon />
                    </Box>
                  </Box>
                  <Box mt={2} display="flex" alignItems="center" gap={0.5}>
                    <TrendingUpIcon color="success" fontSize="small" />
                    <Typography variant="caption" color="success.main" fontWeight="bold">
                      +8.2%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      vs last month
                    </Typography>
                  </Box>
                </CardContent>
              </SummaryCard>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SummaryCard type="expense">
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box>
                      <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                        Total Expenses
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ${summary.totalExpenses.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box className="icon-wrapper">
                      <TrendingDownIcon />
                    </Box>
                  </Box>
                  <Box mt={2} display="flex" alignItems="center" gap={0.5}>
                    <TrendingDownIcon color="error" fontSize="small" />
                    <Typography variant="caption" color="error.main" fontWeight="bold">
                      +2.4%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      vs last month
                    </Typography>
                  </Box>
                </CardContent>
              </SummaryCard>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SummaryCard type="net">
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box>
                      <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                        Net Profit
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ${summary.netIncome.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box className="icon-wrapper">
                      <AccountBalanceIcon />
                    </Box>
                  </Box>
                  <Box mt={2} display="flex" alignItems="center" gap={0.5}>
                    <TrendingUpIcon color="primary" fontSize="small" />
                    <Typography variant="caption" color="primary.main" fontWeight="bold">
                      +12.5%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      vs last month
                    </Typography>
                  </Box>
                </CardContent>
              </SummaryCard>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SummaryCard>
                <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start">
                    <Box>
                      <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                        Total Assets
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        ${assetStats.totalAssetValue.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box className="icon-wrapper">
                      <SavingsIcon />
                    </Box>
                  </Box>
                  <Box mt={2} display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Livestock + Inventory
                    </Typography>
                  </Box>
                </CardContent>
              </SummaryCard>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Assets Breakdown Section */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>Assets & Valuation</Typography>
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                <Box className="icon-wrapper" sx={{ bgcolor: 'primary.light', color: 'primary.main' }}>
                  <PetsIcon />
                </Box>
                <Chip label="Livestock" size="small" color="primary" variant="outlined" />
              </Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                ${assetStats.livestockValue.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current Herd Valuation
              </Typography>
            </CardContent>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                <Box className="icon-wrapper" sx={{ bgcolor: 'warning.light', color: 'warning.main' }}>
                  <InventoryIcon />
                </Box>
                <Chip label="Fixed Assets" size="small" color="warning" variant="outlined" />
              </Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                ${assetStats.inventoryAssetValue.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Machinery & Equipment (Depreciated)
              </Typography>
            </CardContent>
          </SummaryCard>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                <Box className="icon-wrapper" sx={{ bgcolor: 'info.light', color: 'info.main' }}>
                  <AttachMoneyIcon />
                </Box>
                <Chip label="Consumables" size="small" color="info" variant="outlined" />
              </Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                ${assetStats.inventoryConsumableValue.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Feed, Seeds & Supplies
              </Typography>
            </CardContent>
          </SummaryCard>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: '16px', height: '100%', minHeight: 400 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Income vs Expenses Trend</Typography>
            <Box sx={{ height: 320 }}>
              <Bar data={chartData} options={chartOptions} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 3, borderRadius: '16px', height: '100%', minHeight: 400 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Expense Breakdown</Typography>
            <Box sx={{ height: 320, display: 'flex', justifyContent: 'center' }}>
              <Doughnut data={doughnutData} options={{ maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 3, borderRadius: '16px', height: '100%', minHeight: 400 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Asset Allocation</Typography>
            <Box sx={{ height: 320, display: 'flex', justifyContent: 'center' }}>
              <Doughnut data={assetChartData} options={{ maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>}

      {/* Tab section for Transactions and Budget views */}
      <Paper sx={{ width: '100%', borderRadius: '16px', overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}
        >
          <Tab label="Transactions" sx={{ py: 2, fontWeight: 600 }} />
          <Tab label="Budget" sx={{ py: 2, fontWeight: 600 }} />
        </Tabs>

        {/* Transactions tab content */}
        <TabPanel value={tabValue} index={0}>
          {/* Toolbar */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2 }}>
            <TextField
              placeholder="Search transactions..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', md: 300 } }}
            />
            
            <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
               <TextField
                select
                size="small"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                sx={{ minWidth: 150 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="income">Income Only</MenuItem>
                <MenuItem value="expense">Expense Only</MenuItem>
              </TextField>

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newView) => { if(newView) setViewMode(newView); }}
                size="small"
                sx={{ '& .MuiToggleButton-root': { borderRadius: 2, px: 2 } }}
              >
                <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
                <ToggleButton value="list"><ViewListIcon /></ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </Box>

          {filteredTransactions.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <ReceiptIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No transactions found</Typography>
              <Typography variant="body2" color="text.secondary">Try adjusting your filters or search query</Typography>
            </Box>
          ) : viewMode === 'list' ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Amount (KES)</TableCell>
                    <TableCell>Payment Method</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>{new Date(transaction.date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.type}
                          color={transaction.type === 'income' ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>{transaction.category}</TableCell>
                      <TableCell>
                         <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{transaction.description}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{transaction.amount.toLocaleString()}</TableCell>
                      <TableCell>{transaction.paymentMethod}</TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.status}
                          color={
                            transaction.status === 'completed' ? 'success' :
                            transaction.status === 'pending' ? 'warning' : 'default'
                          }
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={(e) => handleMenuClick(e, transaction.id)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Grid container spacing={2}>
              {filteredTransactions.map((transaction) => (
                <Grid item xs={12} sm={6} md={4} key={transaction.id}>
                  <Card sx={{ borderRadius: 3, height: '100%', position: 'relative', overflow: 'visible' }}>
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 12, 
                        right: 12, 
                        zIndex: 1 
                      }}
                    >
                      <IconButton size="small" onClick={(e) => handleMenuClick(e, transaction.id)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Chip 
                          label={transaction.type} 
                          color={transaction.type === 'income' ? 'success' : 'error'} 
                          size="small" 
                          variant="outlined"
                        />
                        <Chip 
                          label={transaction.status} 
                          color={transaction.status === 'completed' ? 'success' : 'warning'} 
                          size="small" 
                        />
                      </Box>
                      <Typography variant="h6" gutterBottom>{transaction.category}</Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>{transaction.description}</Typography>
                      
                      <Divider sx={{ my: 1.5 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                         <Box>
                           <Typography variant="caption" display="block" color="text.secondary">Amount</Typography>
                           <Typography variant="h6" color={transaction.type === 'income' ? 'success.main' : 'error.main'} sx={{ fontWeight: 600 }}>
                             KES {transaction.amount.toLocaleString()}
                           </Typography>
                         </Box>
                         <Box sx={{ textAlign: 'right' }}>
                           <Typography variant="caption" display="block" color="text.secondary">Date</Typography>
                           <Typography variant="body2">{new Date(transaction.date).toLocaleDateString()}</Typography>
                         </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* Budget tab content */}
        <TabPanel value={tabValue} index={1}>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddBudget}>
              Create Budget
            </Button>
          </Box>
          {/* Budget summary cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <SummaryCard>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Total Allocated</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    KES {budgetSummary.totalAllocated.toLocaleString()}
                  </Typography>
                </CardContent>
              </SummaryCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <SummaryCard>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Total Spent</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    KES {budgetSummary.totalSpent.toLocaleString()}
                  </Typography>
                </CardContent>
              </SummaryCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <SummaryCard>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Total Remaining</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: budgetSummary.totalRemaining >= 0 ? 'success.main' : 'error.main' }}>
                    KES {budgetSummary.totalRemaining.toLocaleString()}
                  </Typography>
                </CardContent>
              </SummaryCard>
            </Grid>
          </Grid>

          {/* Budget items table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Allocated (KES)</TableCell>
                  <TableCell>Spent (KES)</TableCell>
                  <TableCell>Remaining (KES)</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Progress</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {budgets.map((budget) => (
                  <TableRow key={budget.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{budget.category}</TableCell>
                    <TableCell>{budget.allocated.toLocaleString()}</TableCell>
                    <TableCell>{budget.spent.toLocaleString()}</TableCell>
                    <TableCell sx={{ color: budget.remaining >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                      {budget.remaining.toLocaleString()}
                    </TableCell>
                    <TableCell>{budget.period}</TableCell>
                    <TableCell sx={{ width: '30%' }}>
                       <Box sx={{ display: 'flex', alignItems: 'center' }}>
                         <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min((budget.spent / budget.allocated) * 100, 100)} 
                              color={budget.spent > budget.allocated ? 'error' : 'primary'}
                              sx={{ height: 10, borderRadius: 5 }}
                            />
                         </Box>
                         <Box sx={{ minWidth: 35 }}>
                           <Typography variant="body2" color="text.secondary">{`${Math.round((budget.spent / budget.allocated) * 100)}%`}</Typography>
                         </Box>
                       </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Context Menu for Transactions */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: '12px', minWidth: '160px' }
        }}
      >
        <MenuItem onClick={() => { 
          const t = transactions.find(t => t.id === selectedTransactionId);
          if (t) handleEditTransaction(t);
        }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          Edit
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => selectedTransactionId && handleDeleteTransaction(selectedTransactionId)} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      {/* Transaction dialog for adding/editing transactions */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTransaction ? 'Edit Transaction' : 'Add New Transaction'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Transaction date field */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={newTransaction.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {/* Transaction type selection */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Type"
                value={newTransaction.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
              >
                <MenuItem value="income">Income</MenuItem>
                <MenuItem value="expense">Expense</MenuItem>
              </TextField>
            </Grid>
            {/* Transaction category selection - dynamically changes based on type */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Category"
                value={newTransaction.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
              >
                {(newTransaction.type === 'income' ? transactionCategories.income : transactionCategories.expense)
                  .map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            {/* Transaction amount field */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Amount (KES)"
                value={newTransaction.amount}
                onChange={(e) => handleInputChange('amount', Number(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
              />
            </Grid>
            {/* Transaction description field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={newTransaction.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>
            {/* Payment method selection */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={newTransaction.paymentMethod}
                onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method} value={method}>
                    {method}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* Transaction reference field */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reference"
                value={newTransaction.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
              />
            </Grid>
            {/* Transaction status selection */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Status"
                value={newTransaction.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        {/* Dialog action buttons */}
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveTransaction} variant="contained" color="primary">
            {editingTransaction ? 'Update' : 'Add'} Transaction
          </Button>
        </DialogActions>
      </Dialog>

      {/* Budget Dialog */}
      <Dialog open={budgetDialog} onClose={() => setBudgetDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Budget</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
             <TextField
                select
                label="Category"
                value={newBudget.category}
                onChange={(e) => setNewBudget({ ...newBudget, category: e.target.value })}
                fullWidth
             >
                {transactionCategories.expense.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
             </TextField>
             <TextField
                type="number"
                label="Allocated Amount (KES)"
                value={newBudget.allocated}
                onChange={(e) => setNewBudget({ ...newBudget, allocated: Number(e.target.value) })}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
             />
             <TextField
                label="Period (Year)"
                value={newBudget.period}
                onChange={(e) => setNewBudget({ ...newBudget, period: e.target.value })}
                fullWidth
             />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBudgetDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveBudget} variant="contained">Save Budget</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FarmFinances;
